// src/pages/admin/AdminUsers.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Button, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Table, TableHead,
  TableRow, TableCell, TableBody, IconButton, Checkbox,
  FormControlLabel, Chip, CircularProgress, Avatar, InputAdornment,
  Tooltip, alpha,
} from "@mui/material";
import DeleteIcon  from "@mui/icons-material/Delete";
import EditIcon    from "@mui/icons-material/Edit";
import AddIcon     from "@mui/icons-material/Add";
import SearchIcon  from "@mui/icons-material/Search";
import SaveIcon    from "@mui/icons-material/Save";
import CloseIcon   from "@mui/icons-material/Close";
import ShieldIcon  from "@mui/icons-material/Shield";
import PersonIcon  from "@mui/icons-material/Person";
import API from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText, indigoGradient } from "../../theme";
import PaginationBar from "../../components/PaginationBar";

const EMPTY = { username:"", email:"", full_name:"", password:"", is_admin:false };

export default function AdminUsers() {
  const toast = useToast();
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search,   setSearch]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, page_size: pageSize });
      if (search) p.set("search", search);
      const { data } = await API.get(`/users/?${p}`);
      setItems(data.items || []); setTotal(data.total || 0); setPages(data.pages || 1);
    } catch { toast.error("Impossible de charger les utilisateurs."); }
    finally { setLoading(false); }
  }, [page, pageSize, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit   = (u) => {
    setEditing(u);
    setForm({ username:u.username||"", email:u.email||"", full_name:u.full_name||"", password:"", is_admin:!!u.is_admin });
    setOpen(true);
  };

  const onSave = async () => {
    if (!form.username?.trim()) { toast.error("Le nom d'utilisateur est requis."); return; }
    if (!editing && !form.password?.trim()) { toast.error("Le mot de passe est requis à la création."); return; }
    setSaving(true);
    try {
      const payload = { username:form.username, email:form.email, full_name:form.full_name||null, is_admin:!!form.is_admin };
      if (form.password) payload.password = form.password;
      if (editing) await API.put(`/users/${editing.id}`, payload);
      else         await API.post("/users/", payload);
      toast.success(editing ? "Utilisateur mis à jour ✓" : "Utilisateur créé ✓");
      setOpen(false); load();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map(d => d?.msg || JSON.stringify(d)).join(" | ")
        : (typeof raw === "string" ? raw : "Erreur de sauvegarde.");
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try { await API.delete(`/users/${id}`); toast.success("Utilisateur supprimé."); load(); }
    catch { toast.error("Suppression impossible."); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const initials = (u) => (u.full_name || u.username || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();

  return (
    <Box>
      <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between" alignItems={{ sm:"center" }} sx={{ mb:4 }} spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">GESTION</Typography>
          <Typography variant="h4" fontWeight={700}><Box component="span" sx={gradientText}>Utilisateurs</Box></Typography>
          <Typography variant="caption" color="text.secondary">{total} utilisateur{total !== 1 ? "s" : ""}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Nouvel utilisateur</Button>
      </Stack>

      <Paper sx={{ p:2, mb:3 }}>
        <TextField placeholder="Rechercher par nom ou email…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ width:{ xs:"100%", sm:340 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color:"text.secondary" }} /></InputAdornment> }} />
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display:"flex", justifyContent:"center", py:8 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Utilisateur</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Rôle</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py:6, color:"text.secondary" }}>Aucun utilisateur trouvé.</TableCell></TableRow>
                ) : items.map(u => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width:34, height:34, fontSize:"0.75rem", fontWeight:800, background: u.is_admin ? indigoGradient : alpha(C.slate[400],.3), color: u.is_admin ? "white" : C.slate[700] }}>
                          {initials(u)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{u.full_name || u.username}</Typography>
                          <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{u.email || "—"}</Typography></TableCell>
                    <TableCell align="center">
                      {u.is_admin
                        ? <Chip icon={<ShieldIcon sx={{ fontSize:"0.85rem !important" }} />} label="Admin" size="small" sx={{ fontWeight:700, background:alpha(C.indigo[500],.1), color:C.indigo[600] }} />
                        : <Chip icon={<PersonIcon sx={{ fontSize:"0.85rem !important" }} />} label="Étudiant" size="small" sx={{ fontWeight:600, background:alpha(C.slate[400],.1), color:C.slate[500] }} />
                      }
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifier"><IconButton size="small" onClick={() => openEdit(u)} sx={{ color:C.indigo[500] }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Supprimer"><IconButton size="small" onClick={() => onDelete(u.id)} sx={{ color:C.red[500] }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ px:2, pb:1 }}>
              <PaginationBar page={page} pages={pages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={p => { setPageSize(p); setPage(1); }} loading={loading} />
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:800, display:"flex", alignItems:"center", justifyContent:"space-between", pb:1 }}>
          {editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display:"flex", flexDirection:"column", gap:2, pt:2.5 }}>
          <Stack direction={{ xs:"column", sm:"row" }} spacing={2}>
            <TextField label="Nom d'utilisateur *" fullWidth value={form.username} onChange={e => set("username", e.target.value)} />
            <TextField label="Email" type="email" fullWidth value={form.email} onChange={e => set("email", e.target.value)} />
          </Stack>
          <TextField label="Nom complet" fullWidth value={form.full_name} onChange={e => set("full_name", e.target.value)} />
          <TextField label={editing ? "Nouveau mot de passe (laisser vide = inchangé)" : "Mot de passe *"}
            type="password" fullWidth required={!editing} value={form.password} onChange={e => set("password", e.target.value)} />
          <FormControlLabel control={<Checkbox checked={!!form.is_admin} onChange={e => set("is_admin", e.target.checked)} />}
            label="Droits administrateur" />
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
          <Button onClick={() => setOpen(false)} variant="outlined" disabled={saving}>Annuler</Button>
          <Button onClick={onSave} variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
            {saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}